import type { ProductUnit, Qty } from '@pos/types';
import { QTY_SCALE } from '@pos/types';

export interface UnitMeta {
  /** Short Russian label shown in the POS. */
  label: string;
  /** Whether fractional quantities are allowed (weighed / measured goods). */
  fractional: boolean;
}

export const UNITS: Record<ProductUnit, UnitMeta> = {
  PIECE: { label: 'шт', fractional: false },
  KG: { label: 'кг', fractional: true },
  G: { label: 'г', fractional: false },
  LITER: { label: 'л', fractional: true },
  ML: { label: 'мл', fractional: false },
  METER: { label: 'м', fractional: true },
  BOX: { label: 'кор', fractional: false },
  PACK: { label: 'уп', fractional: false },
  SET: { label: 'компл', fractional: false },
};

/** Converts a whole/decimal number of units to milli-units: 1.25 → 1250. */
export function toQty(units: number): Qty {
  return Math.round(units * QTY_SCALE);
}

/** One whole unit. */
export const ONE: Qty = QTY_SCALE;

/**
 * Parses cashier input ("2", "1.250", "1,25") into milli-units.
 * Returns null if the input is not a positive quantity with at most 3 decimals.
 */
export function parseQuantity(input: string): Qty | null {
  const cleaned = input.trim().replace(',', '.');
  const match = /^(\d+)(?:\.(\d{0,3}))?$/.exec(cleaned);
  if (!match) return null;
  const qty = Number(match[1]) * QTY_SCALE + Number((match[2] ?? '').padEnd(3, '0'));
  return qty > 0 && Number.isSafeInteger(qty) ? qty : null;
}

export function isValidQuantity(quantity: Qty, unit: ProductUnit): boolean {
  if (!Number.isSafeInteger(quantity) || quantity <= 0) return false;
  return UNITS[unit].fractional || quantity % QTY_SCALE === 0;
}

/** Plain number string without trailing zeros: 1250 → "1.25", 2000 → "2". */
export function formatQuantityValue(quantity: Qty): string {
  const whole = Math.trunc(quantity / QTY_SCALE);
  const fraction = Math.abs(quantity % QTY_SCALE);
  if (fraction === 0) return String(whole);
  const sign = quantity < 0 && whole === 0 ? '-' : '';
  // Russian decimal comma: 1,25 кг (parseQuantity accepts both comma and point).
  return `${sign}${whole},${String(fraction).padStart(3, '0').replace(/0+$/, '')}`;
}

/** Quantity with unit label: 1250 KG → "1,25 кг", 2000 PIECE → "2 шт". */
export function formatQuantity(quantity: Qty, unit: ProductUnit): string {
  return `${formatQuantityValue(quantity)}\u00A0${UNITS[unit].label}`;
}
